import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Theme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  useGetUserSettingsQuery,
  useUpdateUserSettingMutation,
  SavedInvestorFilter,
} from '@/store/settingsApi';
import { useGetTabRecordsQuery } from '@/store/tabsApi';
import { ensureSavedFiltersHaveIds, generateUUID } from '@/utils/savedFilters';
import ManageSavedFiltersDialog from '@/components/reports/ManageSavedFiltersDialog';
import SavedFilterEditor, {
  type FilterCapability,
} from '@/components/modals/SavedFilterEditor';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { WARootState } from '@/store/store';
import { mobileWidth } from '@/constants';
import {
  setExcludedAccounts as setExcludedAccountsAction,
  setExcludedCategories as setExcludedCategoriesAction,
  setExcludedAccountTypes as setExcludedAccountTypesAction,
  setExcludedInstitutions as setExcludedInstitutionsAction,
  setExcludedTags as setExcludedTagsAction,
  setExcludedGroups as setExcludedGroupsAction,
  setFilterClientScope as setFilterClientScopeAction,
} from '@/store/filterSlice';

// Save Filter Modal Component
interface SaveFilterModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (filterName: string) => void;
}

const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [filterName, setFilterName] = useState('');
  const { t } = useTranslation();

  const handleSave = () => {
    if (filterName.trim()) {
      onSave(filterName.trim());
      setFilterName('');
      onClose();
    }
  };

  const handleClose = () => {
    setFilterName('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>{t('saveFilter')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin='dense'
          label={t('filterName')}
          fullWidth
          variant='outlined'
          value={filterName}
          onChange={(e) => {
            setFilterName(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && filterName.trim()) {
              handleSave();
            }
          }}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color='inherit'
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={!filterName.trim()}
        >
          {t('saveFilter')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface ExclusionValues {
  excludedAccounts: number[];
  excludedCategories: number[];
  excludedAccountTypes: number[];
  excludedInstitutions: number[];
  excludedTags: number[];
  excludedGroups: Record<string, string[]>;
}

const EXCLUSION_FIELD_BY_CAPABILITY: Record<
  FilterCapability,
  keyof ExclusionValues
> = {
  accounts: 'excludedAccounts',
  categories: 'excludedCategories',
  types: 'excludedAccountTypes',
  institutions: 'excludedInstitutions',
  tags: 'excludedTags',
  groups: 'excludedGroups',
};

/**
 * What "Reset Filters" should leave behind.
 *
 * The reset button lives inside a tab, but it clears the whole form. Once a view
 * hides tabs it cannot apply (`supportedFilters`), an unscoped reset would drop
 * buckets the user cannot see and that a sibling view still applies — excluded
 * accounts survive on the advisor detailed table but are invisible on the pie.
 * So the reset only touches the capabilities the caller declared. With no
 * declaration — the investor flow, which shows every tab — it stays global.
 */
export function clearedExclusions(
  current: ExclusionValues,
  supportedFilters?: FilterCapability[],
): ExclusionValues {
  const emptied: ExclusionValues = {
    excludedAccounts: [],
    excludedCategories: [],
    excludedAccountTypes: [],
    excludedInstitutions: [],
    excludedTags: [],
    excludedGroups: {},
  };
  if (supportedFilters === undefined) return emptied;

  const next: ExclusionValues = { ...current };
  for (const capability of supportedFilters) {
    const field = EXCLUSION_FIELD_BY_CAPABILITY[capability];
    if (field === 'excludedGroups') {
      next.excludedGroups = emptied.excludedGroups;
    } else {
      next[field] = emptied[field];
    }
  }
  return next;
}

/**
 * Same capability scoping as `clearedExclusions`, for the other way a saved
 * preset can write the form. A preset carries every bucket, so loading one
 * whole would overwrite the ones the view hides — invisible to the user here
 * and still applied by a sibling view. With no declaration the caller shows
 * every tab, so the preset lands intact.
 */
export function appliedSavedFilter(
  current: ExclusionValues,
  incoming: ExclusionValues,
  supportedFilters?: FilterCapability[],
): ExclusionValues {
  if (supportedFilters === undefined) return incoming;

  const next: ExclusionValues = { ...current };
  for (const capability of supportedFilters) {
    const field = EXCLUSION_FIELD_BY_CAPABILITY[capability];
    if (field === 'excludedGroups') {
      next.excludedGroups = incoming.excludedGroups;
    } else {
      next[field] = incoming[field];
    }
  }
  return next;
}

// Main Filter Selection Modal Component
interface FilterSelectionModalProps {
  open?: boolean;
  onClose?: () => void;
  onApply?: (filters: {
    excludedAccounts: number[];
    excludedCategories: number[];
    excludedAccountTypes: number[];
    excludedInstitutions: number[];
    excludedTags?: number[];
    excludedGroups?: Record<string, string[]>;
    selectedFilterName?: string;
    selectedFilterId?: string;
  }) => void;
  initialExcludedAccounts?: number[];
  initialExcludedCategories?: number[];
  initialExcludedAccountTypes?: number[];
  initialExcludedInstitutions?: number[];
  initialExcludedTags?: number[];
  initialExcludedGroups?: Record<string, string[]>;
  initialSelectedFilterName?: string;
  initialSelectedFilterId?: string;
  inline?: boolean;
  supportedFilters?: FilterCapability[];
  /**
   * The client these exclusions belong to, on a surface that has one. It scopes
   * the account options to that client's accounts and records the selection as
   * theirs. Leaving it out is the advisor-wide meaning, and it is not passive:
   * applying then marks the buckets as owned by nobody, so the next scoped page
   * re-scopes instead of inheriting them.
   */
  investorId?: number;
  onFiltersChange?: (filters: {
    excludedAccounts: number[];
    excludedCategories: number[];
    excludedAccountTypes: number[];
    excludedInstitutions: number[];
    excludedTags?: number[];
    excludedGroups?: Record<string, string[]>;
  }) => void;
}

export const FilterSelectionModal: React.FC<FilterSelectionModalProps> = ({
  open = false,
  onClose = () => {},
  onApply,
  initialExcludedAccounts = [],
  initialExcludedCategories = [],
  initialExcludedAccountTypes = [],
  initialExcludedInstitutions = [],
  initialExcludedTags = [],
  initialExcludedGroups = {},
  initialSelectedFilterName = '',
  initialSelectedFilterId = '',
  inline = false,
  onFiltersChange,
  supportedFilters,
  investorId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(mobileWidth);

  // Get user ID and role for Redux persistence
  const userId = useSelector(
    (state: WARootState) => state.appView.UserInfo?.UserId,
  );
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );

  const [selectedFilterIndex, setSelectedFilterIndex] = useState<string>('');
  const [saveFilterModalOpen, setSaveFilterModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const [manageFiltersOpen, setManageFiltersOpen] = useState(false);
  const activeTabInitializedRef = React.useRef(false);

  // Manage all filter state with Formik - persists across tab transitions
  interface FilterFormValues {
    excludedAccounts: number[];
    excludedCategories: number[];
    excludedAccountTypes: number[];
    excludedInstitutions: number[];
    excludedTags: number[];
    excludedGroups: Record<string, string[]>;
  }

  const formik = useFormik<FilterFormValues>({
    enableReinitialize: true,
    initialValues: {
      excludedAccounts: initialExcludedAccounts,
      excludedCategories: initialExcludedCategories,
      excludedAccountTypes: initialExcludedAccountTypes,
      excludedInstitutions: initialExcludedInstitutions,
      excludedTags: initialExcludedTags,
      excludedGroups: initialExcludedGroups,
    },
    onSubmit: () => {
      // Form submission handled by handleApply
      handleApply();
    },
  });

  // Calculate counts from Formik values for chip display
  const excludedAccountsCount = formik.values.excludedAccounts.length;
  const excludedCategoriesCount = formik.values.excludedCategories.length;
  const excludedAccountTypesCount = formik.values.excludedAccountTypes.length;
  const excludedInstitutionsCount = formik.values.excludedInstitutions.length;
  const excludedTagsCount = formik.values.excludedTags.length;
  const excludedGroupsCountByKey: Record<string, number> = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(formik.values.excludedGroups).map(([k, v]) => [
          k,
          v.length,
        ]),
      ),
    [formik.values.excludedGroups],
  );

  // Get tab configuration
  const { data: tabRecords } = useGetTabRecordsQuery('investor-filters');
  const { data: userSettings } = useGetUserSettingsQuery();
  const [updateUserSetting] = useUpdateUserSettingMutation();

  // Parse saved filters from user settings and ensure UUIDs
  const savedFilters = useMemo(() => {
    if (!userSettings) return [];
    const savedFiltersSettings = userSettings.find(
      (setting) => setting.Key === 'SavedInvestorFilters',
    );
    if (!savedFiltersSettings?.Value) return [];
    try {
      const parsedFilters = JSON.parse(
        savedFiltersSettings.Value,
      ) as SavedInvestorFilter[];
      return ensureSavedFiltersHaveIds(parsedFilters);
    } catch (error) {
      console.error('Error parsing saved filters:', error);
      return [];
    }
  }, [userSettings]);

  // Set initial selected filter based on ID or name
  useEffect(() => {
    if (savedFilters.length > 0) {
      let filterIndex = -1;

      // First try to find by ID
      if (initialSelectedFilterId) {
        filterIndex = savedFilters.findIndex(
          (filter) => filter.id === initialSelectedFilterId,
        );
        if (filterIndex !== -1) {
          setSelectedFilterIndex(filterIndex.toString());
        }
      }

      // Fallback to finding by name for backward compatibility
      if (filterIndex === -1 && initialSelectedFilterName) {
        filterIndex = savedFilters.findIndex(
          (filter) => filter.text === initialSelectedFilterName,
        );
        if (filterIndex !== -1) {
          setSelectedFilterIndex(filterIndex.toString());
        }
      }
    }
  }, [initialSelectedFilterId, initialSelectedFilterName, savedFilters]);

  // Set initial active tab when tabs are loaded (only once)
  useEffect(() => {
    if (
      tabRecords &&
      tabRecords.length > 0 &&
      !activeTabInitializedRef.current
    ) {
      setActiveTab(tabRecords[0].TabUIKey);
      activeTabInitializedRef.current = true;
    }
  }, [tabRecords]);

  // Reset Formik values ONLY when the modal is opened (avoids infinite update loop)
  useEffect(() => {
    if (open) {
      void formik.setValues({
        excludedAccounts: initialExcludedAccounts,
        excludedCategories: initialExcludedCategories,
        excludedAccountTypes: initialExcludedAccountTypes,
        excludedInstitutions: initialExcludedInstitutions,
        excludedTags: initialExcludedTags,
        excludedGroups: initialExcludedGroups,
      });
      // Reset activeTab initialization when modal opens so it can be set again
      activeTabInitializedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (inline) {
      onFiltersChange?.({
        excludedAccounts: formik.values.excludedAccounts,
        excludedCategories: formik.values.excludedCategories,
        excludedAccountTypes: formik.values.excludedAccountTypes,
        excludedInstitutions: formik.values.excludedInstitutions,
        excludedTags: formik.values.excludedTags,
        excludedGroups: formik.values.excludedGroups,
      });
    }
  }, [
    formik.values.excludedAccounts,
    formik.values.excludedCategories,
    formik.values.excludedAccountTypes,
    formik.values.excludedInstitutions,
    formik.values.excludedTags,
    formik.values.excludedGroups,
    inline,
    onFiltersChange,
  ]);

  // Convert API ExcludedGroups array to form Record<groupId, tagNames[]>
  const excludedGroupsFromApi = useCallback(
    (excluded: SavedInvestorFilter['filters']['ExcludedGroups']) => {
      if (!excluded?.length) return {};
      return excluded.reduce<Record<string, string[]>>(
        (acc, { GroupId, ExcludedIds }) => {
          acc[GroupId] = ExcludedIds;
          return acc;
        },
        {},
      );
    },
    [],
  );

  // Update Formik values when a saved filter is selected
  const handleSavedFilterSelect = useCallback(
    (filterId: string) => {
      const idx = savedFilters.findIndex((f) => f.id === filterId);
      if (idx !== -1) {
        setSelectedFilterIndex(String(idx));
        const sel = savedFilters[idx];
        void formik.setValues(
          appliedSavedFilter(
            formik.values,
            {
              excludedAccounts: sel.filters.ExcludedAccounts || [],
              excludedCategories: sel.filters.ExcludedCategories || [],
              excludedAccountTypes: sel.filters.ExcludedAccountTypes || [],
              excludedInstitutions: sel.filters.ExcludedInstitutions || [],
              excludedTags: sel.filters.ExcludedTags || [],
              excludedGroups: excludedGroupsFromApi(sel.filters.ExcludedGroups),
            },
            supportedFilters,
          ),
        );
      }
    },
    [savedFilters, formik, excludedGroupsFromApi, supportedFilters],
  );

  const handleSaveFilter = async (filterName: string) => {
    try {
      // Get existing saved filters
      const savedFiltersSettings = userSettings?.find(
        (setting) => setting.Key === 'SavedInvestorFilters',
      );
      let existingFilters: SavedInvestorFilter[] = [];

      if (savedFiltersSettings?.Value) {
        try {
          existingFilters = JSON.parse(
            savedFiltersSettings.Value,
          ) as SavedInvestorFilter[];
        } catch (error) {
          console.error('Error parsing existing filters:', error);
          existingFilters = [];
        }
      }

      const excludedGroupsApi =
        Object.entries(formik.values.excludedGroups).filter(
          ([, ids]) => ids.length > 0,
        ).length > 0
          ? Object.entries(formik.values.excludedGroups)
              .filter(([, ids]) => ids.length > 0)
              .map(([GroupId, ExcludedIds]) => ({ GroupId, ExcludedIds }))
          : undefined;

      const newFilter: SavedInvestorFilter = {
        id: generateUUID(),
        text: filterName,
        filters: {
          limit: null,
          ExcludedCategories:
            formik.values.excludedCategories.length > 0
              ? formik.values.excludedCategories
              : undefined,
          ExcludedAccounts:
            formik.values.excludedAccounts.length > 0
              ? formik.values.excludedAccounts
              : undefined,
          ExcludedAccountTypes:
            formik.values.excludedAccountTypes.length > 0
              ? formik.values.excludedAccountTypes
              : undefined,
          ExcludedInstitutions:
            formik.values.excludedInstitutions.length > 0
              ? formik.values.excludedInstitutions
              : undefined,
          ExcludedTags:
            formik.values.excludedTags.length > 0
              ? formik.values.excludedTags
              : undefined,
          ExcludedGroups: excludedGroupsApi,
        },
      };

      // Add new filter to existing ones
      const updatedFilters = [...existingFilters, newFilter];

      // Save to user settings
      await updateUserSetting({
        Key: 'SavedInvestorFilters',
        Value: JSON.stringify(updatedFilters),
      }).unwrap();
    } catch (error) {
      console.error('Failed to save filter:', error);
    }
  };

  const handleApply = () => {
    const currentFilters = {
      excludedAccounts: formik.values.excludedAccounts,
      excludedCategories: formik.values.excludedCategories,
      excludedAccountTypes: formik.values.excludedAccountTypes,
      excludedInstitutions: formik.values.excludedInstitutions,
      excludedTags: formik.values.excludedTags,
      excludedGroups: formik.values.excludedGroups,
    };

    if (userId && currentRole) {
      dispatch(
        setExcludedAccountsAction({
          userId,
          roleType: currentRole,
          accounts: currentFilters.excludedAccounts,
        }),
      );
      dispatch(
        setExcludedCategoriesAction({
          userId,
          roleType: currentRole,
          categories: currentFilters.excludedCategories,
        }),
      );
      dispatch(
        setExcludedAccountTypesAction({
          userId,
          roleType: currentRole,
          accountTypes: currentFilters.excludedAccountTypes,
        }),
      );
      dispatch(
        setExcludedInstitutionsAction({
          userId,
          roleType: currentRole,
          institutions: currentFilters.excludedInstitutions,
        }),
      );
      dispatch(
        setExcludedTagsAction({
          userId,
          roleType: currentRole,
          tags: currentFilters.excludedTags,
        }),
      );
      dispatch(
        setExcludedGroupsAction({
          userId,
          roleType: currentRole,
          groups: currentFilters.excludedGroups,
        }),
      );
      // Accounts, tags and groups name entities of ONE client, but the bucket is
      // keyed by userId+role only. Stamping the owner here is what keeps the
      // marker honest: without it, picking accounts on an advisor-wide page left
      // the marker pointing at whichever client was opened last, and reopening
      // that client applied the advisor-wide selection to their numbers.
      dispatch(
        setFilterClientScopeAction({
          userId,
          roleType: currentRole,
          clientId: investorId,
        }),
      );
    }

    const selectedFilter =
      selectedFilterIndex !== ''
        ? savedFilters[parseInt(selectedFilterIndex, 10)]
        : undefined;

    onApply?.({
      excludedAccounts: currentFilters.excludedAccounts,
      excludedCategories: currentFilters.excludedCategories,
      excludedAccountTypes: currentFilters.excludedAccountTypes,
      excludedInstitutions: currentFilters.excludedInstitutions,
      excludedTags: currentFilters.excludedTags,
      excludedGroups: currentFilters.excludedGroups,
      selectedFilterName: selectedFilter?.text,
      selectedFilterId: selectedFilter?.id,
    });
    onClose();
  };

  const handleSavedFilterChange = useCallback(
    (update: {
      excludedAccounts: number[];
      excludedCategories: number[];
      excludedAccountTypes: number[];
      excludedInstitutions: number[];
      excludedTags: number[];
      excludedGroups: Record<string, string[]>;
    }) => {
      void formik.setValues({
        excludedAccounts: update.excludedAccounts,
        excludedCategories: update.excludedCategories,
        excludedAccountTypes: update.excludedAccountTypes,
        excludedInstitutions: update.excludedInstitutions,
        excludedTags: update.excludedTags,
        excludedGroups: update.excludedGroups,
      });
    },
    [formik],
  );

  const handleClearAllFilters = useCallback(() => {
    const cleared = clearedExclusions(formik.values, supportedFilters);
    void formik.setValues(cleared);
    handleSavedFilterChange(cleared);
  }, [formik, handleSavedFilterChange, supportedFilters]);

  // Create a stable formik-like object to prevent SavedFilterEditor re-renders
  // Only update when formik.values actually changes (not on every render)
  // formik.setFieldValue is stable, so we can use it directly
  const stableFormik = useMemo(
    () => ({
      values: formik.values,
      setFieldValue: formik.setFieldValue,
    }),
    [formik.values, formik.setFieldValue],
  );

  // InternalDialogContent needs to re-render when Formik values change
  // so that counts update in the chips - counts are calculated from formik.values
  // Memoize the component to prevent unnecessary re-renders
  const InternalDialogContent = useMemo(() => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* Saved Filter Editor */}
        <SavedFilterEditor
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          excludedAccounts={formik.values.excludedAccounts}
          excludedCategories={formik.values.excludedCategories}
          excludedAccountTypes={formik.values.excludedAccountTypes}
          excludedInstitutions={formik.values.excludedInstitutions}
          excludedTags={formik.values.excludedTags}
          excludedGroups={formik.values.excludedGroups}
          onChange={handleSavedFilterChange}
          onClearAllFilters={handleClearAllFilters}
          formik={stableFormik}
          excludedAccountsCount={excludedAccountsCount}
          excludedCategoriesCount={excludedCategoriesCount}
          excludedAccountTypesCount={excludedAccountTypesCount}
          excludedInstitutionsCount={excludedInstitutionsCount}
          excludedTagsCount={excludedTagsCount}
          excludedGroupsCountByKey={excludedGroupsCountByKey}
          supportedFilters={supportedFilters}
          investorId={investorId}
        />
      </Box>
    );
  }, [
    activeTab,
    setActiveTab,
    formik.values.excludedAccounts,
    formik.values.excludedCategories,
    formik.values.excludedAccountTypes,
    formik.values.excludedInstitutions,
    formik.values.excludedTags,
    formik.values.excludedGroups,
    handleSavedFilterChange,
    handleClearAllFilters,
    stableFormik,
    excludedAccountsCount,
    excludedCategoriesCount,
    excludedAccountTypesCount,
    excludedInstitutionsCount,
    excludedTagsCount,
    excludedGroupsCountByKey,
    supportedFilters,
    investorId,
  ]);

  if (inline) {
    return (
      <>
        {InternalDialogContent}
        <SaveFilterModal
          open={saveFilterModalOpen}
          onClose={() => {
            setSaveFilterModalOpen(false);
          }}
          onSave={(filterName) => {
            void handleSaveFilter(filterName);
          }}
        />
        <ManageSavedFiltersDialog
          open={manageFiltersOpen}
          onClose={() => {
            setManageFiltersOpen(false);
          }}
          savedFilters={savedFilters}
          onChangeFilters={handleSavedFilterSelect}
        />
      </>
    );
  }

  return (
    <>
      {/* make the dialog fullscreen with no external padding and round corners when below 1001px */}
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth='md'
        fullWidth
        slotProps={{
          paper: {
            sx: (theme: Theme) => ({
              [theme.breakpoints.down(1001)]: {
                width: '100%',
                height: '100dvh',
                maxWidth: '100%',
                maxHeight: '100dvh',
                margin: 0,
                borderRadius: 0,
                padding: 0,
                paddingTop:
                  'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
                paddingBottom:
                  'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
              },
            }),
          },
        }}
        sx={(theme: Theme) => ({
          [theme.breakpoints.down(1001)]: {
            '& .MuiDialog-container': {
              padding: 0,
              borderRadius: theme.shape.borderRadius,
            },
          },
        })}
      >
        <form
          onSubmit={handleApply}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          }}
        >
          <DialogTitle
            sx={{
              flexShrink: 0,
            }}
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              gap={2}
              flexWrap='wrap'
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Typography variant='h6'>Select What Is Shown</Typography>
              </Box>
              <IconButton
                onClick={onClose}
                size='small'
                sx={{
                  border: 'none !important',
                  boxShadow: 'none !important',
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {InternalDialogContent}
          </DialogContent>

          <DialogActions
            sx={{
              flexShrink: 0,
              px: isMobile ? 3 : 2,
              pb: 1,
            }}
          >
            <Button
              onClick={onClose}
              color='inherit'
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleApply}
              variant='contained'
            >
              {t('applyFilters')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <SaveFilterModal
        open={saveFilterModalOpen}
        onClose={() => {
          setSaveFilterModalOpen(false);
        }}
        onSave={(filterName) => {
          void handleSaveFilter(filterName);
        }}
      />

      <ManageSavedFiltersDialog
        open={manageFiltersOpen}
        onClose={() => {
          setManageFiltersOpen(false);
        }}
        savedFilters={savedFilters}
        onChangeFilters={handleSavedFilterSelect}
      />
    </>
  );
};

export default FilterSelectionModal;
